// ─────────────────────────────────────────────────────────────────────────────
// Agrégation de la masse salariale par équipe pour les graphiques de la page
// Grille salariale. Fonctions PURES, client-safe (aucun import Firestore) — on
// part des RealPayrollRow déjà calculées côté serveur (lib/firestore/real-payroll).
// Coût retenu = salariale (base prorata avec repli grille + heures spéciales +
// primes + avantages) — le boni est agrégé À PART (bonusPotential) et affiché
// dans sa propre carte. Les inactifs sont exclus, comme le total du tableau
// « masse réelle ».
// ─────────────────────────────────────────────────────────────────────────────

import type { RealPayrollRow } from './types'

// Services spécialisés isolés dans leurs propres graphiques (détail par employé).
// Restent des équipes normales : ils comptent aussi dans la répartition globale.
export const SPECIALIZED_SERVICES = ['Securiblocs', 'Axes Conseil'] as const

const NO_TEAM_LABEL = 'Sans équipe'

export type TeamPayroll = {
  teamName: string
  cost: number
  // Boni maximal de l'équipe si toutes les cibles sont atteintes (base × % cible).
  bonusPotential: number
  headcount: number
  isService: boolean
}

export type ServiceBreakdown = {
  name: string
  total: number
  employees: { name: string; cost: number }[]
}

// Salaire de base effectif : taux réel prorata, sinon repli grille — même
// convention que la page Mon équipe et la carte « Part de la masse » du profil.
export function effectiveBaseSalary(row: RealPayrollRow): number {
  return row.unpriced ? (row.gridAnnual ?? 0) : row.annual
}

// Salariale d'une ligne : base effective + heures spéciales + primes + avantages.
// Le boni est volontairement exclu (montré à part via rowBonusPotential).
export function rowCost(row: RealPayrollRow): number {
  return effectiveBaseSalary(row) + row.specialHours + row.premiums + row.benefits
}

// Boni possible d'une ligne : base effective × % cible (0 si aucune cible).
export function rowBonusPotential(row: RealPayrollRow): number {
  return effectiveBaseSalary(row) * (row.bonusTargetPct ?? 0)
}

// Vrai si le nom d'équipe correspond à un service spécialisé (insensible à la casse).
export function isSpecializedService(teamName: string | null): boolean {
  if (!teamName) return false
  const n = teamName.toLowerCase()
  return SPECIALIZED_SERVICES.some((s) => n.includes(s.toLowerCase()))
}

// Regroupe les lignes (actifs seulement) par équipe → coût total + effectif,
// trié par coût décroissant. Les lignes sans équipe tombent sous « Sans équipe ».
export function aggregateByTeam(rows: RealPayrollRow[]): TeamPayroll[] {
  const byTeam = new Map<
    string,
    { cost: number; bonusPotential: number; headcount: number }
  >()
  for (const row of rows) {
    if (!row.active) continue
    const name = row.teamName ?? NO_TEAM_LABEL
    const acc = byTeam.get(name) ?? { cost: 0, bonusPotential: 0, headcount: 0 }
    acc.cost += rowCost(row)
    acc.bonusPotential += rowBonusPotential(row)
    acc.headcount += 1
    byTeam.set(name, acc)
  }
  return [...byTeam.entries()]
    .map(([teamName, { cost, bonusPotential, headcount }]) => ({
      teamName,
      cost,
      bonusPotential,
      headcount,
      isService: isSpecializedService(teamName),
    }))
    .sort((a, b) => b.cost - a.cost)
}

// Détail par employé (actifs) d'un service donné — pour son graphique isolé.
// Retourne null si le service n'a aucun employé chiffré.
export function serviceBreakdown(
  rows: RealPayrollRow[],
  serviceName: string,
): ServiceBreakdown | null {
  const target = serviceName.toLowerCase()
  const employees = rows
    .filter(
      (r) => r.active && (r.teamName?.toLowerCase().includes(target) ?? false),
    )
    .map((r) => ({ name: r.name, cost: rowCost(r) }))
    .filter((e) => e.cost > 0)
    .sort((a, b) => b.cost - a.cost)
  if (employees.length === 0) return null
  const total = employees.reduce((s, e) => s + e.cost, 0)
  return { name: serviceName, total, employees }
}

// Construit les graphiques isolés pour tous les services spécialisés présents.
export function buildServiceBreakdowns(rows: RealPayrollRow[]): ServiceBreakdown[] {
  return SPECIALIZED_SERVICES.map((s) => serviceBreakdown(rows, s)).filter(
    (b): b is ServiceBreakdown => b !== null,
  )
}
